import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const numOrNull = (v) => (v === "" ? null : Number(v));
const cell = "bg-[#0F172A] border border-slate-800 rounded px-2 py-1 text-xs num w-full focus:outline-none focus:border-emerald-500 transition-colors";

const RecordGrid = ({ m, set }) => {
  const setCell = (w, st, v) => set("grid", m.grid.map((row, i) => (i === w ? row.map((x, j) => (j === st ? v : x)) : row)));
  return (
    <div className="scroll-x">
      <table className="text-xs" data-testid={`record-grid-${m.id}`}>
        <thead>
          <tr className="text-slate-500 num">
            <th className="text-left font-normal pr-2 py-1 whitespace-nowrap">Wins \ Starts</th>
            {m.grid[0].map((_, j) => <th key={j} className="font-normal px-0.5">{j + 1}{j === m.grid[0].length - 1 ? "+" : ""}</th>)}
          </tr>
        </thead>
        <tbody>
          {m.grid.map((row, i) => (
            <tr key={i}>
              <td className="pr-2 py-0.5 num text-slate-300">{i === m.grid.length - 1 ? `${i}+` : i}</td>
              {row.map((v, j) => (
                <td key={j} className="px-0.5 py-0.5">
                  {v === null ? <span className="block w-12 text-center text-slate-700">·</span> : (
                    <input data-testid={`record-cell-${i}-${j + 1}`} type="number" step="0.5" value={v ?? ""}
                      onChange={(e) => setCell(i, j, numOrNull(e.target.value))}
                      className={`w-12 bg-[#0F172A] border border-slate-800 rounded px-0.5 py-1 num text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none focus:border-emerald-500 transition-colors ${v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-slate-300"}`} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-500 mt-2">More than 20 starts: wins are scaled to a 20-start equivalent at the same strike rate (e.g. 6 wins from 40 starts → 3 from 20).</p>
    </div>
  );
};

const MatrixCard = ({ m, idx, fields, onChange }) => {
  const set = (k, v) => onChange({ ...m, [k]: v });
  const setBand = (i, k, v) => set("bands", m.bands.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <div className={`panel p-5 fade-up ${m.mode === "grid" ? "md:col-span-2 xl:col-span-3" : ""}`} style={{ animationDelay: `${idx * 50}ms` }} data-testid={`matrix-editor-${m.id}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className="num text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">M{idx + 1}</span>
        <Input data-testid={`matrix-name-input-${m.id}`} value={m.name} onChange={(e) => set("name", e.target.value)} className="h-8 bg-[#0F172A] font-display text-lg font-semibold" />
      </div>
      {m.mode === "grid" ? (<>
        <label className="block w-[90px] mb-3">
          <span className="eyebrow">Weight</span>
          <Input data-testid={`matrix-weight-input-${m.id}`} type="number" step="0.1" value={m.weight} onChange={(e) => set("weight", Number(e.target.value))} className="mt-1 h-8 bg-[#0F172A] num" />
        </label>
        <RecordGrid m={m} set={set} />
      </>) : (<>
      <div className="grid grid-cols-[1fr_90px] gap-3 mb-4">
        <label className="block">
          <span className="eyebrow">Raw Data Field</span>
          <Select value={m.field} onValueChange={(v) => set("field", v)}>
            <SelectTrigger data-testid={`matrix-field-select-${m.id}`} className="mt-1 h-8 bg-[#0F172A] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="block">
          <span className="eyebrow">Weight</span>
          <Input data-testid={`matrix-weight-input-${m.id}`} type="number" step="0.1" value={m.weight} onChange={(e) => set("weight", Number(e.target.value))} className="mt-1 h-8 bg-[#0F172A] num" />
        </label>
      </div>
      <div className="flex bg-[#0F172A] rounded p-1 border border-slate-800 mb-3 w-fit">
        {[["bands", "Band Table"], ["linear", "Points = Value"]].map(([k, l]) => (
          <button key={k} data-testid={`matrix-mode-${m.id}-${k}`} onClick={() => set("mode", k)}
            className={`px-3 py-1 text-xs rounded font-mono uppercase tracking-wider transition-colors ${(m.mode || "bands") === k ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {m.mode === "linear" ? (
        <p className="text-xs text-slate-400" data-testid={`matrix-linear-note-${m.id}`}>Score = raw value × weight (e.g. +2 = two points easier / lighter today, −1.5 = harder / heavier).</p>
      ) : (<>
      <table className="w-full">
        <thead>
          <tr className="eyebrow text-left"><th className="pb-1 font-normal">Min</th><th className="pb-1 font-normal">Max</th><th className="pb-1 font-normal">Points</th><th /></tr>
        </thead>
        <tbody>
          {m.bands.map((b, i) => (
            <tr key={i}>
              <td className="pr-1 py-0.5"><input data-testid={`band-${m.id}-${i}-min`} type="number" step="any" value={b.min ?? ""} onChange={(e) => setBand(i, "min", numOrNull(e.target.value))} className={cell} /></td>
              <td className="pr-1 py-0.5"><input data-testid={`band-${m.id}-${i}-max`} type="number" step="any" value={b.max ?? ""} onChange={(e) => setBand(i, "max", numOrNull(e.target.value))} className={cell} /></td>
              <td className="pr-1 py-0.5"><input data-testid={`band-${m.id}-${i}-points`} type="number" step="any" value={b.points} onChange={(e) => setBand(i, "points", Number(e.target.value))} className={`${cell} text-amber-400`} /></td>
              <td className="py-0.5 w-6">
                <button data-testid={`band-${m.id}-${i}-remove`} onClick={() => set("bands", m.bands.filter((_, j) => j !== i))} className="p-1 text-slate-500 hover:text-rose-400 transition-colors"><X className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button data-testid={`band-${m.id}-add`} onClick={() => set("bands", [...m.bands, { min: null, max: null, points: 0 }])} className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors">
        <Plus className="h-3.5 w-3.5" /> Add band
      </button>
      </>)}
      </>)}
    </div>
  );
};

export const MatricesEditor = ({ matrices, fields, onChange }) => (
  <div className="space-y-4" data-testid="matrices-editor">
    <p className="text-sm text-slate-400">Three rule types: Band Table (raw value matched to the first band where Min ≤ value ≤ Max), Linear (1 point per unit) and Record Grid (wins × starts looked up in the grid, used by Win, Track, Distance, 1st Up, 2nd Up and Wet Track). Points × Weight = rule score. Missing data scores 0.</p>
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {matrices.map((m, i) => (
        <MatrixCard key={m.id} m={m} idx={i} fields={fields} onChange={(nm) => onChange(matrices.map((x, j) => (j === i ? nm : x)))} />
      ))}
    </div>
  </div>
);
